/**
 * StepRail
 *
 * Desktop-only (lg+) vertical navigation sidebar showing all onboarding
 * steps as numbered checkpoints. Used by OnboardingFlow's 2-column layout.
 *
 * - Past steps  → check icon + muted label, clickable (jumps back)
 * - Current     → filled accent circle + bright label
 * - Future      → outlined circle + muted label, not clickable
 *
 * State is fully derived from props — no local state, no side effects.
 */

import { Check } from 'lucide-react';

export interface StepRailItem {
  /** Zero-based step index */
  index: number;
  /** Short label shown in the rail (e.g. "Business Type") */
  label: string;
  /** Optional sub-description shown beneath the label */
  description?: string;
}

export interface StepRailProps {
  items: StepRailItem[];
  currentIndex: number;
  /** Called when the user clicks a completed step. Current/future steps are non-interactive. */
  onNavigate: (index: number) => void;
  /** Disable all navigation (e.g. during a pending mutation) */
  disabled?: boolean;
}

export function StepRail({ items, currentIndex, onNavigate, disabled = false }: StepRailProps) {
  return (
    <ol className="flex flex-col gap-1">
      {items.map((item) => {
        const isPast = item.index < currentIndex;
        const isCurrent = item.index === currentIndex;
        const isClickable = isPast && !disabled;

        return (
          <li key={item.index}>
            <button
              type="button"
              onClick={() => isClickable && onNavigate(item.index)}
              disabled={!isClickable}
              aria-current={isCurrent ? 'step' : undefined}
              className={[
                'group flex w-full items-start gap-4 rounded-xl px-3 py-3 text-left transition-colors',
                isClickable
                  ? 'cursor-pointer hover:bg-white/5'
                  : 'cursor-default',
              ].join(' ')}
            >
              {/* Number / check badge */}
              <span
                className={[
                  'relative mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-sm font-semibold transition-all',
                  isCurrent
                    ? 'border-primary bg-primary/15 text-primary shadow-[0_0_0_4px_rgba(59,130,246,0.12)]'
                    : isPast
                      ? 'border-success/40 bg-success/15 text-success'
                      : 'border-border bg-secondary/40 text-muted',
                ].join(' ')}
              >
                {isPast ? <Check className="h-4 w-4" strokeWidth={3} /> : item.index + 1}
              </span>

              {/* Label stack */}
              <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span
                  className={[
                    'text-sm font-semibold leading-tight transition-colors',
                    isCurrent
                      ? 'text-white'
                      : isPast
                        ? 'text-zinc-300 group-hover:text-white'
                        : 'text-muted',
                  ].join(' ')}
                >
                  {item.label}
                </span>
                {item.description && (
                  <span
                    className={[
                      'text-xs leading-snug transition-colors',
                      isCurrent ? 'text-zinc-400' : 'text-zinc-600',
                    ].join(' ')}
                  >
                    {item.description}
                  </span>
                )}
              </span>
            </button>

            {/* Connector line (skip on the last item) */}
            {item.index < items.length - 1 && (
              <div
                aria-hidden
                className={[
                  'ml-[27px] h-4 w-px',
                  item.index < currentIndex ? 'bg-success/30' : 'bg-border',
                ].join(' ')}
              />
            )}
          </li>
        );
      })}
    </ol>
  );
}
